import { Injectable, OnDestroy, computed, inject, signal } from '@angular/core';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiError } from '../../abstract/api-error';
import { BaseService } from '../../abstract/base.service';
import { OrganizationActivity } from '../data-models/organization-activity.model';
import { ActivityQueryOptions } from '../request-models/activity-query-options.model';
import { ActivityNewPayload } from '../response-models/activity-new-payload.model';
import { Page } from '../response-models/page.model';
import { isRecord } from '../utils/is-record';
import { activityRoleForClaims, isActivityUnseen } from '../utils/organization-activity-utils';
import { nullableString } from '../utils/wire-value-utils';
import { AuthService } from '../services/auth.service';
import { OrganizationContextService } from '../services/organization-context.service';
import { WebSocketService } from '../services/websocket.service';
import { orgPrefix } from '../utils/org-path-utils';

/** The hub's default activity page size. */
const DEFAULT_PAGE_SIZE = 20;
/** Activities shown on the dashboard card. */
const MAX_RECENT_ACTIVITIES = 3;
/** How long a WebSocket activity stays marked new (its enter animation is 300ms). */
const NEW_ACTIVITY_MS = 350;

/**
 * The current organization's activity feed: `GET /organizations/:orgId/activities` (paged with `page` /
 * `limit`), `GET .../activities/last-viewed` and `POST .../activities/mark-seen`
 * (hub `user-activities-controller.ts`). New activities arrive over the WebSocket (`activity.new`).
 *
 * Holds the loaded activities and the last-viewed timestamp as signal state for the dashboard, the
 * activity page and the sidebar/topbar unseen dot. Load failures set `error$`.
 *
 * Reference: /agent-os/specs/2026-01-16-dashboard-updates/spec.md
 */
@Injectable({
  providedIn: 'root'
})
export class OrganizationActivityService extends BaseService<OrganizationActivity> implements OnDestroy {
  private organizationContext = inject(OrganizationContextService);
  private websocketService = inject(WebSocketService);
  private authService = inject(AuthService);
  private destroy$ = new Subject<void>();

  private activitiesSignal = signal<OrganizationActivity[]>([]);
  private loadingActivitiesSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);
  private lastPageSignal = signal<number>(0);
  private hasMoreSignal = signal<boolean>(false);
  private newActivityIdsSignal = signal<Set<string>>(new Set());
  /** When the user last viewed activities (ISO timestamp). */
  private lastViewedActivityAtSignal = signal<string | null>(null);
  /** Prevents duplicate WebSocket subscriptions. */
  private webSocketSubscribed = false;

  readonly activities$ = computed(() => this.activitiesSignal());
  readonly recentActivities$ = computed(() => this.activitiesSignal().slice(0, MAX_RECENT_ACTIVITIES));
  readonly isLoadingActivities$ = computed(() => this.loadingActivitiesSignal());
  readonly error$ = computed(() => this.errorSignal());
  readonly hasMore$ = computed(() => this.hasMoreSignal());
  readonly newActivityIds$ = computed(() => this.newActivityIdsSignal());
  readonly lastViewedActivityAt$ = computed(() => this.lastViewedActivityAtSignal());
  readonly unseenActivities$ = computed(() =>
    this.activitiesSignal().filter(a => isActivityUnseen(a, this.lastViewedActivityAtSignal()))
  );
  readonly unseenActivityCount$ = computed(() => this.unseenActivities$().length);
  /** Whether there are unseen activities (the red dot). */
  readonly hasUnseenActivities$ = computed(() => this.unseenActivityCount$() > 0);

  constructor() {
    super('activities', OrganizationActivity);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Load the first page and the last-viewed timestamp, then listen for new activities. */
  async initialize(): Promise<void> {
    if (!this.organizationContext.currentOrganizationId()) {
      console.warn('[OrganizationActivityService] No organization selected');
      return;
    }
    await Promise.all([this.loadActivities(), this.loadLastViewedActivityAt()]);
    this.subscribeToWebSocket();
  }

  /** One page of an organization's activities, newest first. */
  listActivities(orgId: string, options: ActivityQueryOptions = {}): Observable<Page<OrganizationActivity>> {
    return this.getPage({
      prefix: orgPrefix(orgId),
      query: {
        page: options.page ?? 1,
        limit: options.limit ?? DEFAULT_PAGE_SIZE,
        eventTypes: options.eventTypes?.length ? options.eventTypes.join(',') : undefined,
      },
    });
  }

  /**
   * Load a page of activities into state: page 1 replaces the list, later pages append (skipping any
   * activity already shown, since WebSocket inserts shift the pages). Resolves `null` on failure.
   */
  async loadActivities(options: ActivityQueryOptions = {}): Promise<Page<OrganizationActivity> | null> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) return null;

    this.loadingActivitiesSignal.set(true);
    this.errorSignal.set(null);
    try {
      const page = await firstValueFrom(this.listActivities(orgId, options));
      this.storePage(page);
      return page;
    } catch (error: unknown) {
      const apiError = ApiError.fromHttpError(error);
      console.error('[OrganizationActivityService] Error loading activities:', apiError.message);
      this.errorSignal.set(apiError.errors[0] ?? 'Failed to load activities');
      return null;
    } finally {
      this.loadingActivitiesSignal.set(false);
    }
  }

  /** Load the next page. */
  async loadMoreActivities(): Promise<void> {
    if (!this.hasMoreSignal() || this.loadingActivitiesSignal()) return;
    await this.loadActivities({ page: this.lastPageSignal() + 1 });
  }

  /** Reload from the first page. */
  async refreshActivities(): Promise<void> {
    await this.loadActivities();
  }

  /** Mark activities as seen; the hub returns the new last-viewed timestamp. Failures are logged only. */
  async markActivitiesAsSeen(): Promise<void> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) return;
    try {
      const viewedAt = await firstValueFrom(
        this.request('POST', { prefix: orgPrefix(orgId), path: 'mark-seen' }, toLastViewedAt, {})
      );
      this.lastViewedActivityAtSignal.set(viewedAt ?? new Date().toISOString());
    } catch (error: unknown) {
      console.error('[OrganizationActivityService] Failed to mark activities as seen:', ApiError.fromHttpError(error).message);
    }
  }

  /** Subscribe to `activity.new` once. */
  subscribeToWebSocket(): void {
    if (this.webSocketSubscribed) return;
    this.webSocketSubscribed = true;
    this.websocketService.on<ActivityNewPayload>('activity.new')
      .pipe(takeUntil(this.destroy$))
      .subscribe(payload => this.handleNewActivity(payload));
  }

  /** Whether an activity just arrived (for the enter animation). */
  isNewActivity(activityId: string): boolean {
    return this.newActivityIdsSignal().has(activityId);
  }

  clearError(): void {
    this.errorSignal.set(null);
  }

  getActivities(): OrganizationActivity[] {
    return this.activitiesSignal();
  }

  /** Last viewed timestamp; not critical, so failures are logged only. */
  private async loadLastViewedActivityAt(): Promise<void> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) return;
    try {
      const viewedAt = await firstValueFrom(
        this.request('GET', { prefix: orgPrefix(orgId), path: 'last-viewed' }, toLastViewedAt)
      );
      this.lastViewedActivityAtSignal.set(viewedAt);
    } catch (error: unknown) {
      console.warn('[OrganizationActivityService] Failed to load last viewed timestamp:', ApiError.fromHttpError(error).message);
    }
  }

  private storePage(page: Page<OrganizationActivity>): void {
    const { page: pageNumber, totalPages } = page.paging;
    if (pageNumber <= 1) {
      this.activitiesSignal.set(page.items);
    } else {
      this.activitiesSignal.update(existing => {
        const shown = new Set(existing.map(a => a.id));
        return [...existing, ...page.items.filter(a => !shown.has(a.id))];
      });
    }
    this.lastPageSignal.set(pageNumber);
    this.hasMoreSignal.set(pageNumber < totalPages);
  }

  private handleNewActivity(payload: ActivityNewPayload): void {
    if ((payload.visibilityRoles & this.getUserActivityRole()) === 0) return;
    if (this.activitiesSignal().some(a => a.id === payload.activityId)) return;

    const activity = new OrganizationActivity({
      id: payload.activityId,
      organizationId: this.organizationContext.currentOrganizationId(),
      profileId: payload.profileId,
      eventType: payload.eventType,
      message: payload.message,
      resourceType: payload.resourceType,
      resourceId: payload.resourceId,
      visibilityRoles: payload.visibilityRoles,
      isHighPriority: true, // WebSocket events are high priority
      createdAt: payload.createdAt,
    });
    this.activitiesSignal.update(existing => [activity, ...existing]);
    this.markNew(activity.id);
  }

  private markNew(activityId: string): void {
    this.newActivityIdsSignal.update(ids => new Set(ids).add(activityId));
    setTimeout(() => {
      this.newActivityIdsSignal.update(ids => {
        const next = new Set(ids);
        next.delete(activityId);
        return next;
      });
    }, NEW_ACTIVITY_MS);
  }

  /** The current user's activity visibility role in the current organization. */
  private getUserActivityRole(): number {
    const orgId = this.organizationContext.currentOrganizationId();
    const membership = orgId ? this.authService.getCurrentUser()?.customClaims?.getMembership(orgId) : undefined;
    return activityRoleForClaims(membership?.claims);
  }
}


function toLastViewedAt(data: unknown): string | null {
  return (isRecord(data) ? nullableString(data['lastViewedActivityAt']) : undefined) ?? null;
}
